import { useCallback, useEffect, useRef } from 'react';

// Only explicit navigation is animated; wheel and touch scrolling remain native.
export default function useWellwiredScroll() {
  const active = useRef(null);
  const cancel = useCallback(() => {
    if (!active.current) return;
    cancelAnimationFrame(active.current.frame);
    const onCancel = active.current.onCancel;
    active.current = null;
    onCancel?.();
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cancelOnKey = event => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Escape', 'Tab'].includes(event.key)) cancel();
    };
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('pointerdown', cancel, { passive: true });
    window.addEventListener('keydown', cancelOnKey);
    window.addEventListener('resize', cancel);
    reduced.addEventListener('change', cancel);
    return () => {
      cancel();
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('pointerdown', cancel);
      window.removeEventListener('keydown', cancelOnKey);
      window.removeEventListener('resize', cancel);
      reduced.removeEventListener('change', cancel);
    };
  }, [cancel]);

  const scrollTo = useCallback((top, { onComplete, onCancel, skipRange } = {}) => {
    cancel();
    const start = window.scrollY;
    const end = Math.max(0, Math.min(top, document.documentElement.scrollHeight - window.innerHeight));
    const distance = end - start;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(distance) < 1) {
      window.scrollTo({ top: end, behavior: 'instant' });
      onComplete?.();
      return;
    }

    const direction = Math.sign(distance);
    const skipStart = Math.max(Math.min(start, end), skipRange?.start ?? 0);
    const skipEnd = Math.min(Math.max(start, end), skipRange?.end ?? 0);
    const skipped = Math.max(0, skipEnd - skipStart);
    const visibleDistance = Math.abs(distance) - skipped;
    const beforeSkip = direction > 0 ? skipStart - start : start - skipEnd;
    const duration = Math.min(1200, 650 + Math.sqrt(visibleDistance) * 8);
    const animation = { frame: 0, onCancel };
    let started;
    const step = now => {
      if (active.current !== animation) return;
      if (started === undefined) started = now;
      const t = Math.min(1, (now - started) / duration);
      // Smootherstep has zero velocity and acceleration at both ends.
      const eased = t * t * t * (t * (t * 6 - 15) + 10);
      const travelled = visibleDistance * eased;
      const offset = skipped && travelled > beforeSkip ? skipped : 0;
      const position = t === 1 ? end : start + direction * (travelled + offset);
      window.scrollTo({ top: position, behavior: 'instant' });
      if (t < 1) animation.frame = requestAnimationFrame(step);
      else {
        active.current = null;
        onComplete?.();
      }
    };
    active.current = animation;
    animation.frame = requestAnimationFrame(step);
  }, [cancel]);

  return { scrollTo, cancel };
}
